import { Ionicons } from '@expo/vector-icons';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
	Animated, PanResponder, Platform, Pressable, ScrollView, StyleSheet, useWindowDimensions, View,
	type ViewStyle,
} from 'react-native';

import { useTheme } from '@/components/theme-provider';
import { Modal } from '@/components/ui/modal';
import { Portal } from '@/components/ui/portal';
import { Text } from '@/components/ui/text';
import { useSubmitShortcut } from '@/hooks/use-submit-shortcut';

/**
 * A Gmail-style composer window: floating, draggable, minimizable, resizable — and
 * crucially NOT modal.
 *
 * Why it exists. Writing a reply means reading the thing you are replying to, and a modal
 * makes that impossible: the backdrop blocks the page and the letter you need to quote is
 * behind it. The only way through was to cancel the draft, re-read the message, and start
 * again. So this renders no backdrop at all — the page underneath stays scrollable and
 * clickable while the window sits over it, and the window can be shoved aside or collapsed
 * to a title bar without losing a word of the draft.
 *
 * Structure is fixed on purpose: title bar / scrolling body / pinned action bar. Every
 * composer that used to hand-roll a `maxHeight: Math.max(240, height - 340)` ScrollView
 * inside a 90%-capped modal gets the same guarantee for free — the body takes the leftover
 * height and scrolls, and Send is reachable at any window size.
 *
 * Native gets the same three-part structure inside the ordinary `Modal`. A phone has no
 * desktop to arrange windows on, so drag/minimize/maximize are web-only; the pinned footer
 * is the part that matters on both.
 */

const isWeb = Platform.OS === 'web';

/** Collapsed height — exactly the title bar, docked at the bottom edge. */
const HEADER_H = 44;
/** Gap between the window and the viewport edge when it first docks. */
const DOCK_MARGIN = 24;
const MIN_W = 360;
const MIN_H = 240;

/**
 * The minimum, capped by what the viewport actually has.
 *
 * A bare `Math.max(MIN_W, …)` floor is wider than a 320px browser, and `place()` can only
 * clamp x to 0 — so the right-hand side, INCLUDING the pinned Send button, hung off the
 * screen with no way to reach it. That is the exact failure this window was built to fix, so
 * the floor has to yield to the viewport rather than the other way round.
 */
const floorFor = (min: number, available: number): number => Math.min(min, Math.max(1, available));

/**
 * RN types `cursor` as `'auto' | 'pointer'`; RN-Web passes the full CSS set straight
 * through. One documented cast beats sprinkling `any` at each call site.
 */
const webCursor = (value: string): ViewStyle => ({ cursor: value } as unknown as ViewStyle);

export interface ComposerWindowProps {
	visible: boolean;
	/** Title-bar text. Stays readable when the window is collapsed, so make it name the draft. */
	title: string;
	/** One line under the title. Hidden while minimized. */
	subtitle?: string;
	onDismiss: () => void;
	/**
	 * Fires on Cmd/Ctrl/Alt+Enter, matching `Modal`'s own submit shortcut.
	 *
	 * The handler MUST re-check every condition that disables the Send button. This is a
	 * second entry point into it, and the button's `disabled` prop does not guard it.
	 */
	onSubmit?: () => void;
	/**
	 * True while a send is in flight. Disables the title-bar ✕ as well as the shortcut.
	 *
	 * Without it the ✕ closes mid-send, the composer's own `visible` reset effect wipes the
	 * draft, and a subsequent failure reports "could not send" with the text already gone —
	 * exactly what each composer's "left open with the text intact" catch exists to prevent.
	 */
	busy?: boolean;
	/**
	 * Open collapsed. Used when a draft is restored after a page reload — the user asked for
	 * a page, not for the letter they were writing before, so it announces itself as a title
	 * bar at the bottom edge rather than reopening full size over their work.
	 */
	startMinimized?: boolean;
	/**
	 * Bumped by the host on every explicit open. Clicking Reply while the reply composer is
	 * already open but COLLAPSED should bring it back, not appear to do nothing.
	 */
	expandRequest?: number;
	/** Pinned to the bottom edge — never scrolls away. Put Cancel/Send here. */
	footer?: React.ReactNode;
	initialWidth?: number;
	initialHeight?: number;
	testID?: string;
	children: React.ReactNode;
}

export const ComposerWindow: React.FC<ComposerWindowProps> = ({
	visible, title, subtitle, onDismiss, onSubmit, busy = false, startMinimized = false,
	expandRequest = 0, footer, initialWidth = 640, initialHeight = 640, testID, children,
}) => {
	const theme = useTheme();
	const { width: winW, height: winH } = useWindowDimensions();
	const [minimized, setMinimized] = useState(startMinimized);
	const [maximized, setMaximized] = useState(false);

	useSubmitShortcut({
		enabled: visible && !!onSubmit && !minimized && !busy,
		onSubmit: onSubmit ?? (() => {}),
	});

	/*
	 * Position and size live in refs + Animated.Values rather than React state.
	 *
	 * A drag fires on every pointer move; routing that through `setState` would re-render
	 * the whole composer — a form with a dozen inputs and a live preview — 60 times a
	 * second. Animated.Value writes straight to the DOM style, so dragging stays smooth no
	 * matter how heavy the draft is. The refs are the authoritative copy for the clamping
	 * maths, since an Animated.Value cannot be read synchronously.
	 */
	const posRef = useRef({ x: 0, y: 0 });
	const sizeRef = useRef({ w: initialWidth, h: initialHeight });
	const panX = useRef(new Animated.Value(0)).current;
	const panY = useRef(new Animated.Value(0)).current;
	const wAnim = useRef(new Animated.Value(initialWidth)).current;
	const hAnim = useRef(new Animated.Value(initialHeight)).current;

	/**
	 * Move, clamped so the title bar can never be dragged off-screen and stranded.
	 *
	 * Clamping uses the EFFECTIVE height: while collapsed the window is one title bar tall,
	 * and measuring against the restored height would stop it reaching the bottom edge.
	 */
	const place = useCallback((x: number, y: number) => {
		const w = sizeRef.current.w;
		const h = minimized ? HEADER_H : sizeRef.current.h;
		const cx = Math.min(Math.max(x, 0), Math.max(0, winW - w));
		const cy = Math.min(Math.max(y, 0), Math.max(0, winH - h));
		posRef.current = { x: cx, y: cy };
		panX.setValue(cx);
		panY.setValue(cy);
	}, [winW, winH, minimized, panX, panY]);

	const resize = useCallback((w: number, h: number) => {
		const { x, y } = posRef.current;
		const minW = floorFor(MIN_W, winW);
		const minH = floorFor(MIN_H, winH);
		const cw = Math.min(Math.max(w, minW), Math.max(minW, winW - x));
		const ch = Math.min(Math.max(h, minH), Math.max(minH, winH - y));
		sizeRef.current = { w: cw, h: ch };
		wAnim.setValue(cw);
		hAnim.setValue(ch);
	}, [winW, winH, wAnim, hAnim]);

	/** Where a collapsed window came from, so restoring can put it back. See the effect below. */
	const restoreY = useRef<number | null>(null);

	/** Dock bottom-right on open — Gmail's resting place, and out of the reading column. */
	const opened = useRef(false);
	useEffect(() => {
		if (!visible) {
			opened.current = false;
			setMinimized(startMinimized);
			setMaximized(false);
			// Clearing this MATTERS. Closing while collapsed otherwise leaves a stale y behind,
			// and the restore effect below re-applies it the moment the window is reopened —
			// so the next composer opens at wherever the last one happened to be sitting
			// rather than docked.
			restoreY.current = null;
			return;
		}
		if (opened.current) return;
		opened.current = true;
		const w = Math.min(initialWidth, Math.max(floorFor(MIN_W, winW), winW - DOCK_MARGIN * 2));
		const h = Math.min(initialHeight, Math.max(floorFor(MIN_H, winH), winH - DOCK_MARGIN * 2));
		sizeRef.current = { w, h };
		wAnim.setValue(w);
		hAnim.setValue(h);
		place(winW - w - DOCK_MARGIN, winH - h - DOCK_MARGIN);
	}, [visible, initialWidth, initialHeight, winW, winH, startMinimized, place, wAnim, hAnim]);

	/**
	 * Collapsing drops the window to the bottom edge, and restoring lifts it back.
	 *
	 * A collapsed draft that stayed mid-screen is worse than no minimize at all: the point of
	 * the button is to get the draft out of the way of the thing you are reading, and a title
	 * bar stranded across the middle of the page still covers it.
	 *
	 * DECLARED BEFORE the viewport clamp below, and that order is load-bearing. Both effects
	 * re-run when `minimized` flips (it changes `place`'s identity). If the clamp went first
	 * it would see the collapsed y against the restored height, decide the window no longer
	 * fits, and shrink it to `MIN_H` — a composer that came back from minimize two thirds
	 * shorter than it went in.
	 */
	useEffect(() => {
		if (!visible || !opened.current) return;
		if (minimized) {
			restoreY.current = posRef.current.y;
			place(posRef.current.x, winH);
		} else if (restoreY.current != null) {
			place(posRef.current.x, restoreY.current);
			restoreY.current = null;
		}
	}, [minimized, visible, winH, place]);

	// A window parked against the right edge would hang off it after the browser shrinks.
	useEffect(() => {
		if (!visible || !opened.current) return;
		// Collapsed, the stored size is the size to RESTORE to — leave it alone and just
		// keep the title bar pinned to the bottom edge.
		if (minimized) { place(posRef.current.x, winH); return; }
		resize(sizeRef.current.w, sizeRef.current.h);
		place(posRef.current.x, posRef.current.y);
	}, [winW, winH, visible, minimized, place, resize]);

	// Skips the first run: mounting is not a re-open, and expanding here would undo
	// `startMinimized` on a draft restored after a reload.
	const seenExpand = useRef(expandRequest);
	useEffect(() => {
		if (expandRequest === seenExpand.current) return;
		seenExpand.current = expandRequest;
		setMinimized(false);
	}, [expandRequest]);

	const dragOrigin = useRef({ x: 0, y: 0 });
	const dragResponder = useMemo(() => PanResponder.create({
		onStartShouldSetPanResponder: () => true,
		onPanResponderGrant: () => { dragOrigin.current = { ...posRef.current }; },
		onPanResponderMove: (_e, g) => place(dragOrigin.current.x + g.dx, dragOrigin.current.y + g.dy),
	}), [place]);

	const sizeOrigin = useRef({ w: 0, h: 0 });
	const resizeResponder = useMemo(() => PanResponder.create({
		onStartShouldSetPanResponder: () => true,
		onPanResponderGrant: () => { sizeOrigin.current = { ...sizeRef.current }; },
		onPanResponderMove: (_e, g) => resize(sizeOrigin.current.w + g.dx, sizeOrigin.current.h + g.dy),
	}), [resize]);

	const body = (
		<>
			{/* `display: none` rather than unmounting: collapsing a draft must not scroll the
			    letter back to the top or drop the caret out of the box. */}
			<View style={[styles.body, minimized && styles.hidden]}>
				<ScrollView
					style={styles.scroll}
					contentContainerStyle={styles.scrollContent}
					keyboardShouldPersistTaps="handled"
					keyboardDismissMode="interactive"
				>
					{children}
				</ScrollView>
			</View>
			{footer ? (
				<View
					style={[
						styles.footer,
						minimized && styles.hidden,
						{ borderTopColor: theme.colors.outlineVariant ?? theme.colors.outline },
					]}
				>
					{footer}
				</View>
			) : null}
		</>
	);

	const header = (
		<View style={[styles.header, { backgroundColor: theme.colors.primary }]}>
			<View
				style={[styles.headerGrab, isWeb ? webCursor('move') : null]}
				{...(isWeb && !maximized ? dragResponder.panHandlers : {})}
			>
				<Text style={styles.headerTitle} numberOfLines={1}>{title}</Text>
				{subtitle && !minimized ? (
					<Text style={styles.headerSubtitle} numberOfLines={1}>{subtitle}</Text>
				) : null}
			</View>
			<View style={styles.headerButtons}>
				{isWeb ? (
					<>
						<Pressable
							onPress={() => { setMinimized((v) => !v); setMaximized(false); }}
							accessibilityLabel={minimized ? 'Restore composer' : 'Minimize composer'}
							testID="composer-minimize"
							hitSlop={6}
							style={styles.headerButton}
						>
							<Ionicons name={minimized ? 'chevron-up' : 'remove'} size={18} color="#fff" />
						</Pressable>
						<Pressable
							onPress={() => { setMaximized((v) => !v); setMinimized(false); }}
							accessibilityLabel={maximized ? 'Restore composer size' : 'Maximize composer'}
							testID="composer-maximize"
							hitSlop={6}
							style={styles.headerButton}
						>
							<Ionicons name={maximized ? 'contract-outline' : 'expand-outline'} size={16} color="#fff" />
						</Pressable>
					</>
				) : null}
				{/* Disabled mid-send: closing here would fire the composer's `visible` reset and
				    wipe a draft that may still come back as a failure. */}
				<Pressable
					onPress={busy ? undefined : onDismiss}
					disabled={busy}
					accessibilityLabel="Close composer"
					testID="composer-close"
					hitSlop={6}
					style={styles.headerButton}
				>
					<Ionicons name="close" size={18} color="#fff" />
				</Pressable>
			</View>
		</View>
	);

	if (!visible) return null;

	// Native: no desktop to arrange, so keep the familiar modal — but with the same
	// header / scrolling body / pinned footer split, which is the part that was broken.
	if (!isWeb) {
		return (
			<Modal
				visible={visible}
				/*
					A NO-OP while sending, not `undefined`.

					`Modal` forwards this to React Native's `onRequestClose`, which Android's
					hardware Back button invokes — so Back closed a composer mid-send even though
					the ✕ and the footer buttons were disabled, firing the composer's reset and
					wiping the draft with the request still in flight. If it then rejected there
					was nothing left to retry from. A no-op keeps `onRequestClose` defined, which
					Android warns about when it is missing.
				*/
				onDismiss={busy ? () => {} : onDismiss}
				onSubmit={onSubmit}
				contentContainerStyle={styles.nativeSheet}
			>
				<View style={styles.nativeInner} testID={testID}>
					{header}
					{body}
				</View>
			</Modal>
		);
	}

	const maximizedStyle: ViewStyle = {
		left: DOCK_MARGIN,
		top: DOCK_MARGIN,
		width: winW - DOCK_MARGIN * 2,
		height: winH - DOCK_MARGIN * 2,
		transform: [{ translateX: 0 }, { translateY: 0 }],
	};

	return (
		/*
		 * Portalled to `document.body`, not rendered in place.
		 *
		 * Two reasons, both learned the hard way. `position: fixed` stops anchoring to the
		 * viewport under any ancestor with a transform/filter/contain — of which this app has
		 * several. And in-place the window inherits its mount point's stacking context, so the
		 * left navigation drawer painted straight over a window dragged across it.
		 */
		<Portal>
			<Animated.View
				testID={testID}
				style={[
					styles.window,
					{
						backgroundColor: theme.colors.surface,
						borderColor: theme.colors.outlineVariant ?? theme.colors.outline,
						width: wAnim,
						transform: [{ translateX: panX }, { translateY: panY }],
					},
					// Collapsed to the title bar. Height is set last so it wins over `hAnim`.
					minimized ? { height: HEADER_H } : { height: hAnim },
					maximized ? maximizedStyle : null,
				]}
			>
				{header}
				{body}
				{/* Resize grip. Hidden while maximized or collapsed — there is nothing to resize. */}
				{!maximized && !minimized ? (
					<View
						style={[styles.grip, isWeb ? webCursor('nwse-resize') : null]}
						accessibilityRole="button"
						accessibilityLabel="Resize composer"
						testID="composer-resize"
						{...resizeResponder.panHandlers}
					>
						<Ionicons name="resize-outline" size={13} color={theme.colors.onSurfaceVariant} />
					</View>
				) : null}
			</Animated.View>
		</Portal>
	);
};

const styles = StyleSheet.create({
	window: {
		position: 'fixed' as 'absolute',
		left: 0,
		top: 0,
		// Above the app chrome (headers sit at 1000) but below the notification layers
		// (1500 / 2000) — a toast or an error dialog still has to be readable over a draft.
		zIndex: 1200,
		borderWidth: StyleSheet.hairlineWidth,
		borderRadius: 10,
		overflow: 'hidden',
		shadowColor: '#000',
		shadowOffset: { width: 0, height: 8 },
		shadowOpacity: 0.28,
		shadowRadius: 24,
		elevation: 12,
	},
	/**
	 * A DEFINITE height, not just `maxHeight`.
	 *
	 * `nativeInner` is `flex: 1` (i.e. `flexBasis: 0`). Against a parent whose main-axis size
	 * is auto, Yoga resolves the available space from the content — which is itself zero
	 * because the only child wants to flex — so header, body and footer all collapse to 0 and
	 * `overflow: hidden` clips the sheet to an empty box. Both existing three-part modals in
	 * this repo avoid it the same way: `flex-modal.tsx:110` (`minHeight`) and
	 * `cheat-sheet-modal.tsx:347` (`height: '100%'`).
	 */
	nativeSheet: {
		padding: 0, width: '100%', maxWidth: 640,
		height: '92%', maxHeight: '92%', overflow: 'hidden',
	},
	nativeInner: { flex: 1, minHeight: 0 },
	header: {
		height: HEADER_H,
		flexDirection: 'row',
		alignItems: 'center',
		paddingLeft: 14,
		paddingRight: 4,
	},
	// The grab area is its own View so the title-bar BUTTONS stay clickable: a PanResponder
	// that claims the gesture on touch-start swallows presses from anything inside it.
	headerGrab: { flex: 1, minWidth: 0, justifyContent: 'center', userSelect: 'none' },
	headerTitle: { color: '#fff', fontWeight: '700', fontSize: 14 },
	headerSubtitle: { color: 'rgba(255,255,255,0.82)', fontSize: 11, marginTop: 1 },
	headerButtons: { flexDirection: 'row', alignItems: 'center' },
	headerButton: { paddingHorizontal: 8, paddingVertical: 8 },
	// minHeight:0 is what lets the body actually shrink on RN-Web instead of pushing the
	// pinned footer out through the bottom of the window.
	body: { flex: 1, minHeight: 0 },
	scroll: { flex: 1 },
	scrollContent: { padding: 16 },
	footer: { borderTopWidth: StyleSheet.hairlineWidth, paddingHorizontal: 16, paddingVertical: 12 },
	hidden: { display: 'none' },
	grip: {
		position: 'absolute',
		right: 0,
		bottom: 0,
		width: 22,
		height: 22,
		alignItems: 'center',
		justifyContent: 'center',
	},
});

export default ComposerWindow;
